import { z } from 'zod';

/**
 * Bus event emitted when a member mints a crossing into a linked application
 * (IMM-751.2.1).
 *
 * **The raw token is not here, and must never be.** The payload records that a
 * crossing was minted — who, where from, for which application, and until when —
 * not the secret that authorises it. An event payload is persisted in the outbox
 * and, once audited, in the audit store; a token that reached either would
 * outlive its five minutes in two places it was never meant to exist.
 *
 * `organisationId` is the member's PRIMARY Standort at mint time and is
 * legitimately null: a member may have none, which the parent PRD treats as a
 * valid state rather than an error.
 */
export const AppHandoffMintedPayloadSchema = z.object({
  // The crossing's own row id — NOT the token, and not derived from it. It is
  // what joins a mint to its spend or its refusal in the audit trail, and it
  // authorises nothing: the digest is what an exchange has to present.
  handoffId: z.uuid(),
  workspaceId: z.uuid(),
  memberId: z.uuid(),
  organisationId: z.uuid().nullable(),
  targetId: z.string(),
  expiresAt: z.iso.datetime(),
  occurredAt: z.iso.datetime(),
});

export type AppHandoffMintedPayload = z.infer<typeof AppHandoffMintedPayloadSchema>;

/**
 * Bus event emitted when a crossing is exchanged and the token spent
 * (IMM-751.2.2). Carries no token and no digest — `handoffId` joins it to the
 * mint, which is what an operator asking "who crossed into this application,
 * and when" actually needs.
 */
export const AppHandoffSpentPayloadSchema = z.object({
  handoffId: z.uuid(),
  workspaceId: z.uuid(),
  memberId: z.uuid(),
  organisationId: z.uuid().nullable(),
  targetId: z.string(),
  occurredAt: z.iso.datetime(),
});

export type AppHandoffSpentPayload = z.infer<typeof AppHandoffSpentPayloadSchema>;

/**
 * Bus event emitted when an exchange is refused (IMM-751.2.2).
 *
 * `reason` is the distinction the WIRE deliberately does not carry: every
 * refusal answers the caller with one code, one status and one body, because a
 * caller who could tell them apart could probe which members exist. The operator
 * can tell them apart, here, where the caller cannot read it.
 *
 * `handoffId` and `memberId` are null for `token-invalid`: that branch resolved
 * no row at all, so there is nothing to name — an unknown, expired, spent or
 * wrongly-bound token are one case to this event as they are to the caller.
 * They are populated for `member-inactive`, where a real crossing was burnt.
 *
 * Deliberately carries neither the raw token nor its digest. The audit answers
 * "a crossing into this application was refused", which is what an operator
 * needs, and nothing that could be replayed.
 */
export const AppHandoffRefusedPayloadSchema = z.object({
  handoffId: z.uuid().nullable(),
  workspaceId: z.uuid(),
  memberId: z.uuid().nullable(),
  targetId: z.string(),
  reason: z.enum(['token-invalid', 'member-inactive']),
  occurredAt: z.iso.datetime(),
});

export type AppHandoffRefusedPayload = z.infer<typeof AppHandoffRefusedPayloadSchema>;
